import * as Yup from "yup";
import { validateBmFieldValue } from "./matchFieldValidators";
import { FIELD_NONE, OPERATOR_NONE, rowIsComplete, rowIsStarted } from "./matchRowHelpers";
import { runMatchRowFormSchema } from "./yupFormUtils";
import { descriptionField, nameField } from "./yupCommonFields";

const BM_FIELD_NONE = FIELD_NONE;
const BM_OPERATOR_NONE = OPERATOR_NONE;

function applyBmFieldErrors(row, errors) {
  const result = validateBmFieldValue(row.field, row.value, {
    headerName: row.headerName,
    headerGlob: row.headerGlob,
  });
  if (result.valid) return;

  if (row.field === "headers") {
    if (!String(row.headerName ?? "").trim()) errors.headerName = result.message;
    else if (result.message?.includes("glob")) errors.headerGlob = result.message;
    else errors.headerName = result.message;
  } else {
    errors.value = result.message;
  }
}

const bmMatchRowSchema = Yup.object({
  field: Yup.string().default(BM_FIELD_NONE),
  operator: Yup.string().default(BM_OPERATOR_NONE),
  value: Yup.string().default(""),
  headerName: Yup.string().default(""),
  headerGlob: Yup.string().default(""),
})
  .test("row-field", function (row) {
    if (!rowIsStarted(row, BM_FIELD_NONE, BM_OPERATOR_NONE)) return true;
    if (!row.field || row.field === BM_FIELD_NONE) {
      return this.createError({ path: `${this.path}.field`, message: "Select a field" });
    }
    return true;
  })
  .test("row-operator", function (row) {
    if (!rowIsStarted(row, BM_FIELD_NONE, BM_OPERATOR_NONE)) return true;
    if (!row.field || row.field === BM_FIELD_NONE) return true;
    if (!row.operator || row.operator === BM_OPERATOR_NONE) {
      return this.createError({ path: `${this.path}.operator`, message: "Select an operator" });
    }
    return true;
  })
  .test("row-value", function (row) {
    if (!rowIsStarted(row, BM_FIELD_NONE, BM_OPERATOR_NONE)) return true;
    if (!row.field || row.field === BM_FIELD_NONE) return true;
    if (!row.operator || row.operator === BM_OPERATOR_NONE) return true;

    const errors = {};
    applyBmFieldErrors(row, errors);
    const firstKey = Object.keys(errors)[0];
    if (firstKey) {
      return this.createError({ path: `${this.path}.${firstKey}`, message: errors[firstKey] });
    }
    return true;
  });

export const bmExceptionFormSchema = Yup.object({
  name: nameField,
  description: descriptionField,
  // BM-only required expiry (do not use shared expiresAtField.required — that field
  // still treats trimmed-empty as valid for DDoS/firewall/rate-limit).
  expiresAt: Yup.string()
    .trim()
    .required("Expires at is required")
    .test(
      "expiresAt-valid",
      "Enter a valid expiration date/time",
      (v) => !Number.isNaN(new Date(v).getTime()),
    ),
  matchRows: Yup.array()
    .of(bmMatchRowSchema)
    .test("matchRows-required", function (rows) {
      const list = rows || [];
      const started = list.filter((row) =>
        rowIsStarted(row, BM_FIELD_NONE, BM_OPERATOR_NONE),
      );
      const fullyValid = list.filter((row) => {
        if (!rowIsComplete(row, BM_FIELD_NONE, BM_OPERATOR_NONE)) return false;
        const errors = {};
        applyBmFieldErrors(row, errors);
        return Object.keys(errors).length === 0;
      });

      if (fullyValid.length > 0) return true;

      if (started.length === 0) {
        return this.createError({ message: "At least one match criterion is required" });
      }

      const complete = started.filter((row) =>
        rowIsComplete(row, BM_FIELD_NONE, BM_OPERATOR_NONE),
      );
      if (complete.length === 0) {
        return this.createError({
          message: "Complete at least one match criterion or remove empty rows",
        });
      }

      return true;
    }),
});

export function validateBmExceptionForm(values) {
  const result = runMatchRowFormSchema(bmExceptionFormSchema, values);
  if (result.valid) return result;

  const rows = values.matchRows || [];
  const anyStarted = rows.some((row) =>
    rowIsStarted(row, BM_FIELD_NONE, BM_OPERATOR_NONE),
  );
  if (
    !anyStarted &&
    result.formErrors.matchRows === "At least one match criterion is required" &&
    rows.length > 0
  ) {
    result.rowErrors[0] = {
      ...result.rowErrors[0],
      field: result.rowErrors[0].field || "Select a field",
      operator: result.rowErrors[0].operator || "Select an operator",
    };
  }

  return result;
}
